import { useEffect, useState } from "react";
import { FilterIcon, InboxIcon, LayersIcon, SparklesIcon } from "lucide-react";

import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Button } from "@ui/button";

import { type SearchIteration } from "@/stores/search/iterationSlice";
import { searchStore } from "@/stores/search";
import { SearchNudgeKind } from "@/types";

// Ratios are over decisions made, not cards shown — undecided cards shouldn't
// dilute a run of hides. Below this many decisions the ratio is noise.
const MIN_DECISIONS_FOR_RATIOS = 5;
const REVIEW_PILE_THRESHOLD = 20;
const NOTHING_LANDING_HIDE_RATIO = 0.7;
const INTERESTING_EVERYONE_RATIO = 0.85;
const INTERESTING_EVERYONE_MIN_DECISIONS = 10;
const DEEP_SCROLL_PAGES = 5;

interface Nudge {
  kind: SearchNudgeKind;
  Icon: typeof FilterIcon;
  title: string;
  body: string;
  action: "filters" | "review";
  actionLabel: string;
}

// First rule that fires and hasn't shown this iteration wins. Ordered by how
// much the staffer loses by ignoring it.
const pickNudge = ({
  iteration,
  reviewQueueCount,
}: {
  iteration: SearchIteration;
  reviewQueueCount: number;
}): Nudge | null => {
  const { hideCount, interestCount, pagesLoaded, shownNudges } = iteration;
  const decisions = hideCount + interestCount;
  const hasEnoughDecisions = decisions >= MIN_DECISIONS_FOR_RATIOS;
  const isShown = (kind: SearchNudgeKind) => shownNudges.includes(kind);

  if (
    !isShown(SearchNudgeKind.REVIEW_PILE) &&
    reviewQueueCount >= REVIEW_PILE_THRESHOLD
  ) {
    return {
      kind: SearchNudgeKind.REVIEW_PILE,
      Icon: InboxIcon,
      title: `${String(reviewQueueCount)} waiting in review`,
      body: "Clear your review pile before adding more — reviewing shows what to tune.",
      action: "review",
      actionLabel: `Review ${String(reviewQueueCount)}`,
    };
  }
  if (
    !isShown(SearchNudgeKind.NOTHING_LANDING) &&
    hasEnoughDecisions &&
    hideCount / decisions >= NOTHING_LANDING_HIDE_RATIO
  ) {
    return {
      kind: SearchNudgeKind.NOTHING_LANDING,
      Icon: FilterIcon,
      title: "Mostly hides so far",
      body: "That's usually the keywords, not the pool. Think about adjusting them before scrolling on.",
      action: "filters",
      actionLabel: "Edit keywords",
    };
  }
  if (
    !isShown(SearchNudgeKind.INTERESTING_EVERYONE) &&
    decisions >= INTERESTING_EVERYONE_MIN_DECISIONS &&
    interestCount / decisions >= INTERESTING_EVERYONE_RATIO
  ) {
    return {
      kind: SearchNudgeKind.INTERESTING_EVERYONE,
      Icon: SparklesIcon,
      title: "You've saved almost everyone",
      body: "Check the review pile before going deeper. If they all look right the search is rolling; if not, tighten it.",
      action: "review",
      actionLabel: `Review ${String(reviewQueueCount)}`,
    };
  }
  if (
    !isShown(SearchNudgeKind.DEEP_SCROLL) &&
    pagesLoaded >= DEEP_SCROLL_PAGES
  ) {
    return {
      kind: SearchNudgeKind.DEEP_SCROLL,
      Icon: LayersIcon,
      title: `Page ${String(pagesLoaded)}`,
      body: "Ranking gets thin down here. A new keyword beats another page.",
      action: "filters",
      actionLabel: "Edit keywords",
    };
  }
  return null;
};

/**
 * Injected between result pages. Watches the current iteration and shows one
 * nudge so a fast run doesn't turn into a doom scroll: review pile building
 * up, mostly hides, saving everyone, or going too deep. Once a nudge fires it
 * stays put.
 */
export const SearchPageBreak = ({
  reviewQueueCount,
}: {
  reviewQueueCount: number;
}) => {
  /* React Hooks */
  const { iteration, markNudgeShown, requestFiltersDialog, setIsReviewOpen } =
    searchStore();
  // Keep evaluating until something fires, then pin it so the card doesn't
  // change under the reader.
  const [pinnedNudge, setPinnedNudge] = useState<Nudge | null>(null);
  const liveNudge =
    iteration && !pinnedNudge
      ? pickNudge({ iteration, reviewQueueCount })
      : null;
  const nudge = pinnedNudge ?? liveNudge;
  useEffect(() => {
    if (!liveNudge) return;
    setPinnedNudge(liveNudge);
    markNudgeShown(liveNudge.kind);
  }, [liveNudge, markNudgeShown]);

  /* Handlers */
  const handleAction = () => {
    if (!nudge) return;
    if (nudge.action === "review") {
      setIsReviewOpen(true);
      return;
    }
    requestFiltersDialog();
  };

  /* Render */
  const render = () => {
    if (!nudge) {
      return (
        <Row className="items-center gap-3 py-3">
          <Row className="h-px flex-1 bg-slate-200" />
          <Text className="text-xs font-normal text-slate-400">
            Page {String((iteration?.pagesLoaded ?? 0) + 1)}
          </Text>
          <Row className="h-px flex-1 bg-slate-200" />
        </Row>
      );
    }
    const { Icon } = nudge;
    return (
      <Row className="my-3 items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
        <Icon className="h-4 w-4 shrink-0 text-blue-600" />
        <Row className="min-w-0 flex-1 flex-wrap items-baseline gap-x-2">
          <Text className="text-sm font-semibold text-blue-900">
            {nudge.title}
          </Text>
          <Text className="text-sm font-normal text-blue-800">
            {nudge.body}
          </Text>
        </Row>
        <Button
          className="h-8 shrink-0 rounded-full"
          onClick={handleAction}
          size="sm"
          type="button"
        >
          {nudge.actionLabel}
        </Button>
      </Row>
    );
  };

  return render();
};
